import { Localized } from "@fluent/react/compat";
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from "react";

import { useToggleState } from "coral-framework/hooks";
import { useMutation } from "coral-framework/lib/relay";
import { GQLUSER_ROLE, GQLUSER_ROLE_RL } from "coral-framework/schema";
import {
  Button,
  ButtonIcon,
  ClickOutside,
  Dropdown,
  Popover,
} from "coral-ui/components/v2";

import { UserRoleChangeContainer_user } from "coral-admin/__generated__/UserRoleChangeContainer_user.graphql";

import SiteRoleModal from "./SiteRoleModal";
import UpdateUserBadgesMutation from "./UpdateUserBadgesMutation";
import UserRoleChangeButton from "./UserRoleChangeButton";
import UserRoleText from "./UserRoleText";

import styles from "./UserRoleChange.css";

interface Props {
  username: string | null;
  userID: string;
  onChangeRole: (role: GQLUSER_ROLE_RL) => Promise<void>;
  onChangeModerationScopes: (siteIDs: string[]) => Promise<void>;
  onChangeMembershipScopes: (siteIDs: string[]) => Promise<void>;
  role: GQLUSER_ROLE_RL;
  moderationScoped?: boolean;
  membershipScoped?: boolean;
  moderationScopes: UserRoleChangeContainer_user["moderationScopes"];
  membershipScopes: UserRoleChangeContainer_user["membershipScopes"];
  moderationScopesEnabled?: boolean;
  siteUsernames?: UserRoleChangeContainer_user["siteUsername"];
}

const UserRoleChange: FunctionComponent<Props> = ({
  username,
  userID,
  role,
  membershipScoped,
  moderationScoped,
  onChangeRole,
  onChangeModerationScopes,
  moderationScopes,
  onChangeMembershipScopes,
  membershipScopes,
  moderationScopesEnabled = false,
  siteUsernames = [],
}) => {
  // Setup state and callbacks for the popover.
  const [
    isPopoverVisible,
    setPopoverVisibility,
    togglePopoverVisibility,
  ] = useToggleState();

  const updateUserBadges = useMutation(UpdateUserBadgesMutation);

  /**
   * handleChangeRole combines the change role function with the change
   * moderation scopes.
   */
  const handleChangeRole = useCallback(
    async (r: GQLUSER_ROLE_RL, siteIDs: string[] = []) => {
      await onChangeRole(r);

      if (r === GQLUSER_ROLE.MEMBER) {
        await onChangeMembershipScopes(siteIDs);
      } else if (r === GQLUSER_ROLE.MODERATOR && moderationScopesEnabled) {
        await onChangeModerationScopes(siteIDs);
      }
    },
    [
      onChangeMembershipScopes,
      onChangeRole,
      onChangeModerationScopes,
      moderationScopesEnabled,
    ]
  );
  const onClick = useCallback(
    (r: GQLUSER_ROLE_RL, siteIDs: string[] = []) => async () => {
      await handleChangeRole(r, siteIDs);
      togglePopoverVisibility();
    },
    [handleChangeRole, togglePopoverVisibility]
  );

  const [siteRole, setSiteRole] = useState<GQLUSER_ROLE | null>(null);

  const onFinishModal = useCallback(
    async (siteIDs: string[], siteBadges: Record<string, string>) => {
      try {
        await handleChangeRole(siteRole!, siteIDs);
        if (siteRole === GQLUSER_ROLE.MEMBER && siteIDs.length > 0) {
          // Filter to only include sites that have a badge value set (including empty string for "No Badge")
          const filteredSiteIDs = siteIDs.filter((siteID) => {
            return siteID in siteBadges;
          });
          for (const siteID of filteredSiteIDs) {
            const newBadge = siteBadges[siteID]?.trim() || "";
            const badgeInput = newBadge
              ? [{ badge: newBadge, isUserSpecific: true }]
              : [];

            await updateUserBadges({
              userID,
              siteID,
              badges: badgeInput,
            });
          }
        }

        setSiteRole(null);
      } catch (err) {
        setSiteRole(null);
        throw err;
      }
    },
    [siteRole, handleChangeRole, updateUserBadges, userID]
  );

  const selectedModerationSiteIDs = useMemo(
    () =>
      moderationScopes?.sites
        ?.filter((site) => site && typeof site.id === "string")
        .map((site) => site.id),
    [moderationScopes]
  );

  const selectedMembershipSiteIDs = useMemo(
    () =>
      membershipScopes?.sites
        ?.filter((site) => site && typeof site.id === "string")
        .map((site) => site.id),
    [membershipScopes]
  );

  const showSiteRoleModal = !!siteRole;
  const siteRoleSiteIDs = useMemo(() => {
    if (siteRole === GQLUSER_ROLE.MODERATOR) {
      return selectedModerationSiteIDs;
    }
    if (siteRole === GQLUSER_ROLE.MEMBER) {
      return selectedMembershipSiteIDs;
    }
    return undefined;
  }, [siteRole, selectedModerationSiteIDs, selectedMembershipSiteIDs]);

  // Pre-fill badge dropdown with existing user-specific badges
  const existingSiteBadges = useMemo(() => {
    if (!siteUsernames?.length || !siteRoleSiteIDs?.length) {
      return {};
    }

    return siteUsernames.reduce<Record<string, string>>((acc, siteUsername) => {
      if (
        siteUsername?.siteID &&
        siteRoleSiteIDs.includes(siteUsername.siteID)
      ) {
        const userBadge = siteUsername.badges?.find((b) => b?.isUserSpecific);
        if (userBadge) {
          acc[siteUsername.siteID] = userBadge.badge;
        }
      }
      return acc;
    }, {});
  }, [siteUsernames, siteRoleSiteIDs]);

  return (
    <>
      <SiteRoleModal
        roleToBeSet={siteRole}
        username={username}
        open={showSiteRoleModal}
        selectedSiteIDs={siteRoleSiteIDs}
        initialSiteBadges={existingSiteBadges}
        siteUsernames={siteUsernames}
        onCancel={() => setSiteRole(null)}
        onFinish={(siteIDs: string[], siteBadges: Record<string, string>) =>
          onFinishModal(siteIDs, siteBadges)
        }
      />
      <Localized id="community-role-popover" attrs={{ description: true }}>
        <Popover
          id="community-roleChange"
          placement="bottom-start"
          description="A dropdown to change the user role"
          visible={isPopoverVisible}
          body={
            <ClickOutside onClickOutside={togglePopoverVisibility}>
              <Dropdown>
                <UserRoleChangeButton
                  active={role === GQLUSER_ROLE.COMMENTER}
                  role={GQLUSER_ROLE.COMMENTER}
                  moderationScopesEnabled={moderationScopesEnabled}
                  onClick={onClick(GQLUSER_ROLE.COMMENTER)}
                />
                <UserRoleChangeButton
                  active={membershipScoped && role === GQLUSER_ROLE.MEMBER}
                  role={GQLUSER_ROLE.MEMBER}
                  moderationScopesEnabled={moderationScopesEnabled}
                  scoped
                  onClick={() => {
                    setSiteRole(GQLUSER_ROLE.MEMBER);
                    setPopoverVisibility(false);
                  }}
                />
                <UserRoleChangeButton
                  active={role === GQLUSER_ROLE.STAFF}
                  role={GQLUSER_ROLE.STAFF}
                  moderationScopesEnabled={moderationScopesEnabled}
                  onClick={onClick(GQLUSER_ROLE.STAFF)}
                />
                {moderationScopesEnabled && (
                  <UserRoleChangeButton
                    active={moderationScoped && role === GQLUSER_ROLE.MODERATOR}
                    role={GQLUSER_ROLE.MODERATOR}
                    scoped
                    moderationScopesEnabled
                    onClick={() => {
                      setSiteRole(GQLUSER_ROLE.MODERATOR);
                      setPopoverVisibility(false);
                    }}
                  />
                )}
                <UserRoleChangeButton
                  active={
                    (!moderationScopesEnabled ||
                      (moderationScopesEnabled && !moderationScoped)) &&
                    role === GQLUSER_ROLE.MODERATOR
                  }
                  role={GQLUSER_ROLE.MODERATOR}
                  moderationScopesEnabled={moderationScopesEnabled}
                  onClick={onClick(GQLUSER_ROLE.MODERATOR)}
                />
                <UserRoleChangeButton
                  active={role === GQLUSER_ROLE.ADMIN}
                  role={GQLUSER_ROLE.ADMIN}
                  moderationScopesEnabled={moderationScopesEnabled}
                  onClick={onClick(GQLUSER_ROLE.ADMIN)}
                />
              </Dropdown>
            </ClickOutside>
          }
        >
          {({ ref }) => (
            <Localized
              id="community-changeRoleButton"
              attrs={{ "aria-label": true }}
            >
              <Button
                aria-label="Change role"
                className={styles.button}
                onClick={togglePopoverVisibility}
                uppercase={false}
                size="large"
                color="mono"
                ref={ref}
                variant="text"
              >
                <UserRoleText
                  moderationScopesEnabled={moderationScopesEnabled}
                  scoped={moderationScoped || membershipScoped}
                  role={role}
                />
                <ButtonIcon size="lg">
                  {isPopoverVisible ? "arrow_drop_up" : "arrow_drop_down"}
                </ButtonIcon>
              </Button>
            </Localized>
          )}
        </Popover>
      </Localized>
    </>
  );
};

export default UserRoleChange;
